<script setup lang="ts">
/**
 * Перечень фич проекта — поверхность управления фичами.
 *
 * Норма: spec/features/business-model/features.md (R01, R05, R08, R14),
 * spec/features/business-model/feature-lifecycle.md (R01, R04, R11, R14, R17,
 * R19, R27, R28).
 *
 * Живёт действием шапки рядом с селектором фичи, а не разделом оболочки
 * (R27, основание: вердикт 275). Отсюда доступны все операции над фичей как
 * над объектом: заведение, переименование и изменение описания со сменой
 * статуса (диалог свойств, R04), удаление с подтверждением (R11, R14).
 * Перечень содержит HEAD-фичи, не удалённые и не архивные, от новых к старым —
 * его состав определяет продукт (R01), клиент ничего не досчитывает;
 * переключатель добавляет архивные, и над ними доступен возврат из архива
 * (R28).
 *
 * Удержания и каскад клиент не проверяет: они свойство продукта, и обойти их
 * выбором поверхности нельзя (R24). Отказ по удержанию приходит с признаком
 * причины и параметрами и показывается как есть (R13).
 */
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  ElButton,
  ElCheckbox,
  ElDialog,
  ElEmpty,
  ElMessage,
  ElMessageBox,
  ElTable,
  ElTableColumn,
  ElTag,
} from "element-plus";

import { useFeaturesStore } from "@/entities/features";
import { useApiErrorMessage } from "@/shared/lib";
import type { IFeature } from "@/shared/types";
import MbFeatureFormDialog from "./MbFeatureFormDialog.vue";

defineOptions({ name: "MbFeatureManagerDialog" });

const props = defineProps<{
  modelValue: boolean;
  /** Выбранный проект: перечень и все операции живут в его границах. */
  projectId: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const { t } = useI18n();
const { showApiError } = useApiErrorMessage();
const featuresStore = useFeaturesStore();

const showForm = ref(false);
/** Фича, чьи свойства открыты; `null` — заведение новой (R01, R04). */
const editedFeature = ref<IFeature | null>(null);

/**
 * Показывать архивные фичи (R28). По умолчанию перечень их не содержит —
 * тот же состав, что у перечня фич проекта (features-R01).
 */
const showArchived = ref(false);

/** Строки таблицы: перечень как его отдал продукт, без досчёта (R01, R28). */
const rows = computed<IFeature[]>(() =>
  showArchived.value
    ? [...featuresStore.featuresWithArchived]
    : [...featuresStore.features],
);

function isArchived(feature: IFeature): boolean {
  return feature.status === "archived";
}

/** Типизация строки таблицы Element Plus — см. docs/ELEMENT_PLUS_GUIDE.md. */
function asFeature(row: unknown): IFeature {
  return row as IFeature;
}

/** Перечитать тот перечень, который сейчас показан (R01, R28). */
async function reload(): Promise<void> {
  if (props.projectId.length === 0) return;
  if (showArchived.value) {
    await featuresStore.loadFeaturesWithArchived(props.projectId);
    return;
  }
  await featuresStore.loadFeatures(props.projectId);
}

/**
 * Открытие поверхности перечитывает перечень: продукт мог измениться другим
 * пользователем (R20).
 */
watch(
  () => props.modelValue,
  async (opened) => {
    if (opened) await reload();
  },
);

/** Переключатель показа архивных запрашивает перечень заново (R28). */
watch(showArchived, async () => {
  if (props.modelValue) await reload();
});

/**
 * Любая операция перечитывает базовый перечень (R03, R08, R17). Когда показаны
 * архивные, перечень поверхности приходится обновить отдельно: это другой
 * состав, и продукт его не присылал.
 */
watch(
  () => featuresStore.features,
  async () => {
    if (props.modelValue && showArchived.value) {
      await featuresStore.loadFeaturesWithArchived(props.projectId);
    }
  },
);

function statusTagType(feature: IFeature): "info" | "warning" | undefined {
  if (feature.status === "merged") return undefined;
  return feature.status === "archived" ? "info" : "warning";
}

function openCreate(): void {
  editedFeature.value = null;
  showForm.value = true;
}

function openProperties(feature: IFeature): void {
  editedFeature.value = feature;
  showForm.value = true;
}

/**
 * Удаление фичи (R11, R14). Подтверждение обязательно: оно называет фичу,
 * предупреждает о каскаде и необратимости и сообщает, что скрыть фичу,
 * сохранив содержимое, можно архивацией (R10, R18).
 *
 * Отмена подтверждения не выполняет ничего и ничего не сообщает — отмена
 * пользователем и отказ продукта различимы (api-errors-R09).
 */
async function confirmDelete(feature: IFeature): Promise<void> {
  try {
    await ElMessageBox.confirm(
      [
        t("feature.deleteCascade"),
        t("feature.deleteIrreversible"),
        t("feature.deleteArchiveHint"),
      ].join("\n\n"),
      t("feature.deleteTitle", { name: feature.name }),
      {
        confirmButtonText: t("common.delete"),
        cancelButtonText: t("common.cancel"),
        type: "warning",
      },
    );
  } catch {
    return;
  }

  await featuresStore.deleteFeature(props.projectId, feature.id);

  if (featuresStore.error !== null) {
    showApiError(featuresStore.error);
    return;
  }

  // После успеха перечень перечитан, а выбор — если удалена выбранная фича —
  // перешёл на новейшую HEAD (R17, R19).
  ElMessage.success(t("feature.deleted", { name: feature.name }));
}
</script>

<template>
  <ElDialog
    :model-value="modelValue"
    :title="t('feature.manageTitle')"
    width="760px"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="feature-manager__toolbar">
      <!-- Архивные фичи наблюдаемы и возвращаются отсюда (R28) -->
      <ElCheckbox v-model="showArchived">
        {{ t("feature.showArchived") }}
      </ElCheckbox>
      <ElButton type="primary" @click="openCreate">
        {{ t("feature.create") }}
      </ElButton>
    </div>

    <ElEmpty v-if="rows.length === 0" :description="t('feature.listEmpty')" />

    <ElTable v-else v-loading="featuresStore.isLoading" :data="rows">
      <ElTableColumn :label="t('feature.name')" prop="name" min-width="180">
        <template #default="{ row }">
          <span>{{ asFeature(row).name }}</span>
          <ElTag
            v-if="asFeature(row).id === featuresStore.selectedFeatureId"
            size="small"
            type="success"
            class="feature-manager__tag"
          >
            {{ t("feature.selected") }}
          </ElTag>
        </template>
      </ElTableColumn>

      <ElTableColumn :label="t('feature.status')" width="140">
        <template #default="{ row }">
          <ElTag size="small" :type="statusTagType(asFeature(row))">
            {{ t(`feature.statuses.${asFeature(row).status}`) }}
          </ElTag>
        </template>
      </ElTableColumn>

      <ElTableColumn
        :label="t('feature.description')"
        prop="description"
        min-width="200"
        show-overflow-tooltip
      />

      <!--
        Над архивной фичей доступен только возврат из архива: ни удаления, ни
        диалога свойств в её строке нет — название правится в самом диалоге
        возврата (R28).
      -->
      <ElTableColumn :label="t('common.actions')" width="220">
        <template #default="{ row }">
          <ElButton
            link
            type="primary"
            @click="openProperties(asFeature(row))"
          >
            {{ isArchived(asFeature(row)) ? t("feature.restore") : t("feature.properties") }}
          </ElButton>
          <ElButton
            v-if="!isArchived(asFeature(row))"
            link
            type="danger"
            :loading="featuresStore.isDeleting"
            @click="confirmDelete(asFeature(row))"
          >
            {{ t("common.delete") }}
          </ElButton>
        </template>
      </ElTableColumn>
    </ElTable>

    <MbFeatureFormDialog
      v-model="showForm"
      :project-id="projectId"
      :feature="editedFeature"
    />

    <template #footer>
      <ElButton @click="emit('update:modelValue', false)">
        {{ t("common.close") }}
      </ElButton>
    </template>
  </ElDialog>
</template>

<style scoped>
.feature-manager__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.feature-manager__tag {
  margin-left: 8px;
}
</style>
